import React, { useMemo, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useLocale } from '../contexts/LocaleContext';
import { PANEL_TABLE_SCROLL_STYLE } from '../constants/layout';
import { stripHtml } from '../utils/note-utils';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { SortableTableHeader } from './SortableTableHeader';
import { EntityRelevanceDisplay } from './EntityRelevanceDisplay';
import { AnalyzeButton } from './AnalyzeButton';

export interface LocationsTableRow {
  id: string;
  name: string;
  description?: string;
  narrativeFunction?: string;
  historicalCorrection?: string;
  improvements?: string;
  isManual?: boolean;
  relevance?: number;
}

interface LocationsTableProps {
  locations: LocationsTableRow[];
  analyzingLocationId: string | null;
  onRowClick: (location: LocationsTableRow) => void;
  onAnalyzeLocation: (location: LocationsTableRow) => void;
  analyzeTooltip: string;
}

export const LocationsTable: React.FC<LocationsTableProps> = ({
  locations,
  analyzingLocationId,
  onRowClick,
  onAnalyzeLocation,
  analyzeTooltip,
}) => {
  const { t } = useLocale();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'relevance', desc: true }]);

  const columns = useMemo<ColumnDef<LocationsTableRow>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <SortableTableHeader label={t('name')} column={column} />,
        cell: ({ row }) => (
          <span className="min-w-[18rem] font-medium whitespace-nowrap">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'relevance',
        header: ({ column }) => (
          <SortableTableHeader label={t('entity_relevance')} column={column} />
        ),
        cell: ({ row }) => <EntityRelevanceDisplay value={row.original.relevance} />,
        sortingFn: (rowA, rowB) =>
          (rowA.original.relevance ?? 0) - (rowB.original.relevance ?? 0),
      },
      {
        accessorKey: 'narrativeFunction',
        header: ({ column }) => (
          <SortableTableHeader label={t('location_narrative_function')} column={column} />
        ),
        cell: ({ row }) => (
          <span className="block max-w-[250px] truncate text-muted-foreground">
            {row.original.narrativeFunction || '—'}
          </span>
        ),
        sortingFn: (rowA, rowB) =>
          (rowA.original.narrativeFunction || '').localeCompare(
            rowB.original.narrativeFunction || ''
          ),
      },
      {
        accessorKey: 'description',
        header: ({ column }) => (
          <SortableTableHeader label={t('description')} column={column} />
        ),
        cell: ({ row }) =>
          row.original.description ? (
            <div
              className={`line-clamp-2 max-w-md text-muted-foreground ${RICH_TEXT_HTML_CLASS} [&_p]:my-0`}
              dangerouslySetInnerHTML={{ __html: row.original.description }}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        sortingFn: (rowA, rowB) =>
          stripHtml(rowA.original.description || '').localeCompare(
            stripHtml(rowB.original.description || '')
          ),
      },
      {
        id: 'analysis',
        accessorFn: (row) => Boolean(row.historicalCorrection || row.improvements),
        header: ({ column }) => <SortableTableHeader label={t('analysis')} column={column} />,
        cell: ({ row }) => {
          const location = row.original;
          const hasAnalysis = Boolean(location.historicalCorrection || location.improvements);
          return (
            <div
              className="flex items-center gap-2"
              onClick={(event) => event.stopPropagation()}
            >
              <AnalyzeButton
                onClick={() => onAnalyzeLocation(location)}
                isAnalyzing={analyzingLocationId === location.id}
                tooltip={analyzeTooltip}
                matchLabelWidth={t('reanalyze')}
              >
                {hasAnalysis ? t('reanalyze') : t('analyze')}
              </AnalyzeButton>
            </div>
          );
        },
      },
    ],
    [t, analyzingLocationId, onAnalyzeLocation, analyzeTooltip]
  );

  const table = useReactTable({
    data: locations,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div style={PANEL_TABLE_SCROLL_STYLE}>
      <Table className="min-w-[800px]">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer"
              onClick={() => onRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
